/**
 * Cession de droits : durée stockée en jours en base (approx. pour l’UI).
 * La date de fin, elle, s’ajoute en calendrier selon l’unité choisie
 * (jours / semaines / mois), pas via un forçage * 30.
 */

export type DurationUnit = 'days' | 'weeks' | 'months';

export const DAYS_PER_MONTH = 30;
export const DAYS_PER_WEEK = 7;

export function daysToDisplay(days: number): { value: number; unit: DurationUnit } {
  if (days <= 0) return { value: 12, unit: 'months' };
  if (days % DAYS_PER_MONTH === 0) return { value: days / DAYS_PER_MONTH, unit: 'months' };
  if (days % DAYS_PER_WEEK === 0) return { value: days / DAYS_PER_WEEK, unit: 'weeks' };
  return { value: days, unit: 'days' };
}

/** Persist duration as days (1 month ≈ 30, 1 week = 7). Used for storage/display round-trip. */
export function toDays(value: number, unit: DurationUnit): number {
  const n = Math.max(1, Math.floor(value));
  switch (unit) {
    case 'months': return n * DAYS_PER_MONTH;
    case 'weeks': return n * DAYS_PER_WEEK;
    default: return n;
  }
}

/** Ajoute N jours à une date (calendrier local, sans décalage UTC). */
export function addDaysToLocalDate(date: Date, days: number): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + Math.max(0, Math.floor(days)));
  return d;
}

/**
 * Ajoute une durée calendaire selon l’unité UI.
 * Mois : même jour du mois (clamp si besoin, ex. 31 jan + 1 mois → 28/29 fév).
 */
export function addDurationToLocalDate(date: Date, value: number, unit: DurationUnit): Date {
  const n = Math.max(0, Math.floor(value));
  const base = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  switch (unit) {
    case 'months': {
      const day = base.getDate();
      const target = new Date(base.getFullYear(), base.getMonth() + n, 1);
      const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
      target.setDate(Math.min(day, lastDay));
      return target;
    }
    case 'weeks':
      return addDaysToLocalDate(base, n * DAYS_PER_WEEK);
    default:
      return addDaysToLocalDate(base, n);
  }
}

/** True if rights end date is strictly before today (local calendar). */
export function isRightsEndExpired(endDate: Date | string): boolean {
  const end = endDate instanceof Date ? endDate : new Date(endDate);
  if (Number.isNaN(end.getTime())) return false;
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return endDay.getTime() < today.getTime();
}

type T = (key: string) => string;

/** Affiche une durée en jours en "X mois" / "X semaines" / "X jours" (i18n). */
export function formatRightsDurationLabel(days: number, t: T): string {
  if (days >= DAYS_PER_MONTH && days % DAYS_PER_MONTH === 0) {
    const n = days / DAYS_PER_MONTH;
    return `${n} ${n === 1 ? t('projects.durationMonthSingular') : t('projects.durationMonthsPlural')}`;
  }
  if (days >= DAYS_PER_WEEK && days % DAYS_PER_WEEK === 0) {
    const n = days / DAYS_PER_WEEK;
    return `${n} ${n === 1 ? t('projects.durationWeekSingular') : t('projects.durationWeeksPlural')}`;
  }
  return `${days} ${days === 1 ? t('projects.durationDaySingular') : t('projects.durationDaysPlural')}`;
}
